import { DragHandle } from '@mui/icons-material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FileCopyIcon from '@mui/icons-material/FileCopyRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { Checkbox, Snackbar, Tooltip, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useUserData } from 'hooks/use-user-data';
import React from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Survey } from '../survey.types';
import { ImageTypes, Routes } from '../../../enums';
import { getSurveyInviteLink, getSurveyWebReportLink } from '../../../API/surveys';
import PdfReportButton from './pdf-report-button';

interface Props {
  survey: Survey;
  index: number;
  isSelected: boolean;
  isGhosting: boolean;
  selectionCount: number;
  dynamicRoute: string;
  token: string;

  handleCheckboxClick(analysisId: string): (event: React.ChangeEvent, checked: boolean) => void;
}

const SurveyItemDetails: React.FC<Props> = ({ survey, index, handleCheckboxClick, isSelected, isGhosting, selectionCount, dynamicRoute, token }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenant } = useUserData();

  const [state, setState] = React.useState({
    open: false,
  });

  const copyKeyToClipboard = async () => {
    setState({
      open: true,
    });

    setTimeout(() => {
      setState({
        ...state,
        open: false,
      });
    }, 3000);

    const fallbackLink = `${dynamicRoute}/${survey.id}?tenantId=${tenant}`;
    const inviteLinkResult = await getSurveyInviteLink(survey.id);
    const inviteLink = !('error' in inviteLinkResult) && inviteLinkResult.url ? inviteLinkResult.url : fallbackLink;
    return navigator.clipboard.writeText(inviteLink);
  };

  const showEmailAndName = () => {
    if (survey.firstname === undefined && survey.lastname === undefined) {
      return `${survey.email}`;
    } else if (survey.lastname === undefined) {
      return `${survey.email} | ${survey.firstname}`;
    } else if (survey.firstname === undefined) {
      return `${survey.email} | ${survey.lastname}`;
    } else {
      return `${survey.email} | ${survey.firstname} ${survey.lastname}`;
    }
  };

  const openWebReport = async () => {
    const linkResult = await getSurveyWebReportLink(survey.id);
    if (!('error' in linkResult) && linkResult.token) {
      navigate(`${Routes.MOTIVE_WEB_REPORT}/${linkResult.token}`);
      return;
    }

    // Fallback to raw survey id route shape so the user is not blocked.
    navigate(`${Routes.MOTIVE_WEB_REPORT}/${survey.id}`);
  };

  return (
    <>
      <Draggable draggableId={survey.id} index={index}>
        {(provided, snapshot) => {
          const shouldShowSelectionBubble: boolean = snapshot.isDragging && selectionCount > 1;
          return (
            <div
              {...provided.draggableProps}
              ref={provided.innerRef}
              className={cx(classes.paper, {
                [classes.selected]: isSelected,
                [classes.beingDragged]: snapshot.isDragging && !isGhosting,
                [classes.beingGhost]: isGhosting,
              })}
            >
              <div className={classes.title}>
                {survey.type === ImageTypes.MODERN_IMAGE ? (
                  <Tooltip title={t('modern')!}>
                    <RocketLaunchIcon className={classes.icon} />
                  </Tooltip>
                ) : survey.type === ImageTypes.CLASSIC_IMAGE ? (
                  <Tooltip title={t('classic')!}>
                    <AccountBalanceIcon className={classes.icon} />
                  </Tooltip>
                ) : survey.type === ImageTypes.EXTENDED_IMAGE ? (
                  <Tooltip title={t('extended')!}>
                    <AddCircleIcon className={classes.icon} />
                  </Tooltip>
                ) : undefined}
                <Typography variant="body1">{showEmailAndName()}</Typography>
                {!survey.emailSent && (
                  <Tooltip title={<Typography variant="caption">{t('warningNoEmailSent')}</Typography>} placement="right">
                    <WarningRoundedIcon color="error" className={classes.warningIcon} />
                  </Tooltip>
                )}
              </div>
              <div className={classes.title}>
                <PdfReportButton completed={survey.completed} surveyId={survey.id} token={token} />
                <Tooltip title={t('copyKey')!}>
                  <IconButton onClick={copyKeyToClipboard} className={classes.copyButton} size="large">
                    <FileCopyIcon fontSize={'small'} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open Web Report">
                  <IconButton onClick={openWebReport} className={classes.copyButton} size="large">
                    <OpenInNewIcon fontSize={'small'} />
                  </IconButton>
                </Tooltip>
                <div className={classes.title} {...provided.dragHandleProps}>
                  <Checkbox onChange={handleCheckboxClick(survey.id)} />
                  <div className={classes.dragHandleContainer}>
                    <DragHandle />
                  </div>
                </div>
              </div>
              {shouldShowSelectionBubble && <div className={classes.selectionCount}>{selectionCount}</div>}
            </div>
          );
        }}
      </Draggable>
      <div>
        <Snackbar
          open={state.open}
          ContentProps={{
            'aria-describedby': 'message-id',
          }}
          message={<span id="message-id">{t('keyCopied', { key: survey.id })}</span>}
        />
      </div>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    display: 'flex',
    width: '100%',
    marginBottom: theme.spacing(0.5),
    alignItems: 'center',
    backgroundColor: theme.palette.grey[300],
    height: 40,
    padding: theme.spacing(1),
    justifyContent: 'space-between',
    borderRadius: '5px',
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    marginRight: theme.spacing(2),
    color: theme.palette.grey[700],
  },
  warningIcon: {
    marginLeft: theme.spacing(1),
  },
  copyButton: {
    marginLeft: theme.spacing(1),
  },
  dragHandleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
    color: theme.palette.grey[700],
  },
  beingGhost: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.common.white,
  },
  selected: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  beingDragged: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.common.white,
  },
  dragHandler: {
    paddingTop: '6.1px', // Bug in the dnd-styles-applied
    marginLeft: theme.spacing(1),
  },
  title: {
    display: 'flex',
    alignItems: 'center',
  },
  selectionCount: {
    right: -12.5,
    top: -12.5,
    color: theme.palette.common.black,
    backgroundColor: theme.palette.secondary.main,
    borderRadius: '50%',
    height: 25,
    width: 25,
    // lineHeight: 25,
    position: 'absolute',
    textAlign: 'center',
    fontSize: '1rem',
    textDecoration: 'bold',
  },
  root: {
    display: 'flex',
    alignItems: 'center',
  },
}));

export default React.memo(SurveyItemDetails);
